import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faVideo,
  faGlobe,
  faArrowRight,
  faChalkboardTeacher,
  faStar,
  faXmark,
  faHourglassHalf,
  faCircleExclamation,
  faCircleCheck,
  faTriangleExclamation,
  faCalendarPlus,
  faBook,
  faCheckCircle,
  faExternalLinkAlt,
  faLaptop,
  faVolumeHigh,
  faDoorOpen,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import WallOfFame from '../components/Awards/WallOfFame';
import InviteFriends from './InviteFriends';
import CompetitionTeaser from './CompetitionTeaser';
import './Dashboard.css';

/* Only save a real place time zone ("Asia/Beirut"). A device set to a raw offset gives
   "Etc/GMT+8", "UTC" or "GMT" — no city, no flag, often wrong — and used to overwrite the
   time zone chosen in the admin dashboard on every sign-in. */
const isRealZone = (tz) => typeof tz === 'string' && tz.includes('/') && !tz.startsWith('Etc/');

const API = process.env.REACT_APP_API_BASE_URL;
const JOIN_EARLY_MIN = 5;
const CLASS_LENGTH_MIN = 60;

const shortName = (first = '', last = '') => (last ? `${first} ${last[0].toUpperCase()}.` : first || 'Student');

const formatCountdown = (ms) => {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d} day${d > 1 ? 's' : ''} ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

const fmtDate = (iso, opts) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', opts) : '';
};

const Stars = ({ rating }) => {
  const r = Math.round(Number(rating) || 0);
  if (r <= 0) return null; // no fake default ratings
  return (
    <div className="rmsd-stars" aria-label={`Rated ${r} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <FontAwesomeIcon key={n} icon={faStar} className={n <= r ? 'is-on' : ''} />
      ))}
    </div>
  );
};

const StudentDashboard = ({ userData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [joining, setJoining] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, message }

  const studentId = userData?.id;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const waUrl = (text) =>
    `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

  // ---------- Dashboard data (same endpoint) ----------
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/student/dashboard/${studentId}`);
        if (!cancelled) {
          setData(response.data);
          setError('');
        }
      } catch (err) {
        if (!cancelled) setError('We couldn’t load your dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // ---------- Save the student's time zone (same logic as before) ----------
  useEffect(() => {
    if (!studentId || !isRealZone(timeZone)) return;
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } };
    (async () => {
      try {
        const offsetHours = -new Date().getTimezoneOffset() / 60;
        const res = await axios.get(`${API}/api/student/dashboard/OffsetList`, auth);

        let list = res.data;
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list =
            (Array.isArray(list.data) && list.data) ||
            (Array.isArray(list.recordset) && list.recordset) ||
            (Array.isArray(list.result) && list.result) ||
            Object.values(list).filter((item) => item && typeof item === 'object' && item.Id !== undefined);
        }
        if (!Array.isArray(list)) list = [];

        let settingId = list.find((s) => s.Timezone === timeZone)?.Id ?? null;
        if (!settingId) {
          const added = await axios.post(`${API}/api/student/dashboard/addTimezone`, { timezone: timeZone, offsetHours }, auth);
          settingId = added.data.id;
        }
        if (settingId) {
          await axios.put(`${API}/api/student/dashboard/updateOffset/${studentId}`, { offsetId: settingId }, auth);
        }
      } catch (err) {
        console.error('Error detecting/updating timezone:', err);
      }
    })();
  }, [studentId, timeZone]);

  // ---------- Clock for the countdown ----------
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Escape closes the dialog
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => e.key === 'Escape' && setDialog(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog]);

  // ---------- Join (same server checks) ----------
  const handleJoinClass = async (classId, isLive) => {
    // When live, open the tab right away so phones/Safari don't block Zoom
    const zoomTab = isLive ? window.open('', '_blank') : null;
    setJoining(true);
    try {
      const response = await axios.post(`${API}/api/classes/check-availability`, { classId });
      const { status, message, zoomUrl } = response.data;

      if (status === 'joining') {
        const joinResponse = await axios.post(`${API}/api/classes/confirm-join/studentjoin`, { classId });
        if (joinResponse.data.success && zoomUrl) {
          if (zoomTab) zoomTab.location.href = zoomUrl;
          else window.open(zoomUrl, '_blank');
          return;
        }
        zoomTab?.close();
        setDialog({ type: 'error', message: 'We couldn’t open the class. Please try again.' });
        return;
      }

      zoomTab?.close();
      if (status === 'early') setDialog({ type: 'early', message });
      else if (status === 'expired') setDialog({ type: 'expired', message });
      else setDialog({ type: 'error', message: message || 'Please try again.' });
    } catch (err) {
      zoomTab?.close();
      console.error('Join error:', err);
      setDialog({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setJoining(false);
    }
  };

  // ---------- Loading ----------
  if (loading || !studentId) {
    return (
      <div className="rmsd" aria-busy="true">
        <div className="rmsd-inner">
          <div className="rmsd-skel rmsd-skel--title" />
          <div className="rmsd-stats">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmsd-skel rmsd-skel--stat" />
            ))}
          </div>
          <div className="rmsd-grid">
            <div className="rmsd-skel rmsd-skel--card" />
            <div className="rmsd-skel rmsd-skel--card" />
          </div>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error || !data) {
    return (
      <div className="rmsd">
        <div className="rmsd-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmsd-state-icon" />
          <h1 className="rmsd-state-title">Something went wrong</h1>
          <p>{error || 'We couldn’t find your dashboard.'} Please try again in a moment.</p>
          <div className="rmsd-state-actions">
            <button type="button" className="rmsd-btn" onClick={() => window.location.reload()}>
              Try again
            </button>
            <a className="rmsd-btn rmsd-btn--wa" href={waUrl('Hi! My student dashboard isn’t loading.')} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faWhatsapp} /> Message us
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Next class ----------
  const firstName = userData?.firstName || userData?.fullName?.split(' ')[0] || 'there';
  const hasNext = parseInt(data.schedulenext, 10) !== 0 && data.classDateTime;
  const start = hasNext ? new Date(data.classDateTime) : null;
  const validStart = start && !Number.isNaN(start.getTime());

  let status = 'none';
  if (validStart) {
    const diff = start - now;
    if (diff > JOIN_EARLY_MIN * 60000) status = 'upcoming';
    else if (diff > -CLASS_LENGTH_MIN * 60000) status = 'live';
    else status = 'ended';
  }

  const teacher = data.Teacher || 'your teacher';
  const classTitle = data.nextSessionText || 'Your next class';
  const dateLabel = validStart ? start.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
  const timeLabel = validStart ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  // Add to calendar
  const end = validStart ? new Date(start.getTime() + 60 * 60000) : null;
  const icsDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const calTitle = `RoboMind class: ${classTitle}`;
  const calDetails = `Live class with ${teacher}. Join from your dashboard: ${window.location.origin}/student/dashboard`;
  const googleCalUrl = validStart
    ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calTitle)}&dates=${icsDate(
        start
      )}/${icsDate(end)}&details=${encodeURIComponent(calDetails)}`
    : '';

  const downloadIcs = () => {
    if (!validStart) return;
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RoboMind//Student Class//EN',
      'BEGIN:VEVENT',
      `UID:robomind-class-${data.scheduleid || icsDate(start)}@robomind-edu.com`,
      `DTSTAMP:${icsDate(new Date())}`,
      `DTSTART:${icsDate(start)}`,
      `DTEND:${icsDate(end)}`,
      `SUMMARY:${calTitle}`,
      `DESCRIPTION:${calDetails}`,
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Your class starts in 15 minutes',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robomind-class.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  const recent = (data.recentCourses || []).slice(0, 3);
  const showcase = (data.recentCoursesByStudents || []).slice(0, 3);
  const completed = Number(data.nbofclasstaken) || 0;
  const remaining = Number(data.ongoingclass) || 0; // sessions left in the current package

  return (
    <div className="rmsd">
      <div className="rmsd-inner">
        {/* ---------- Greeting ---------- */}
        <header className="rmsd-head">
          <span className="rmsd-eyebrow">My Dashboard</span>
          <h1 className="rmsd-title">
            Welcome back, <span>{firstName}</span>!
          </h1>
        </header>

        {/* ---------- Monthly challenge (at the top: most students join the class without scrolling) ---------- */}
        <CompetitionTeaser />

        {/* ---------- Progress ---------- */}
        <div className="rmsd-stats">
          <div className="rmsd-stat">
            <span className="rmsd-stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </span>
            <div>
              <strong>{completed}</strong>
              <span>classes completed</span>
            </div>
          </div>
          <div className={`rmsd-stat ${remaining <= 2 ? 'is-low' : ''}`}>
            <span className="rmsd-stat-icon">
              <FontAwesomeIcon icon={faBook} />
            </span>
            <div>
              <strong>{remaining}</strong>
              <span>{remaining === 1 ? 'session left in your package' : 'sessions left in your package'}</span>
              {remaining <= 2 && (
                <a
                  className="rmsd-renew"
                  href={waUrl(`Hi! I'm ${userData?.fullName || 'a student'} and I have ${remaining} session${remaining === 1 ? '' : 's'} left. I'd like to renew my package.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Renew package <FontAwesomeIcon icon={faArrowRight} />
                </a>
              )}
            </div>
          </div>
          <div className="rmsd-stat">
            <span className="rmsd-stat-icon">
              <FontAwesomeIcon icon={faClock} />
            </span>
            <div>
              <strong>{status === 'upcoming' ? formatCountdown(start - now) : status === 'live' ? 'Now' : '—'}</strong>
              <span>until next class</span>
            </div>
          </div>
        </div>

        <div className="rmsd-grid">
          {/* ---------- Next class ---------- */}
          <section className={`rmsd-card ${status === 'live' ? 'rmsd-card--live' : ''}`} aria-labelledby="rmsd-card-title">
            {status === 'none' || status === 'ended' ? (
              <>
                <div className="rmsd-card-top">
                  <span className="rmsd-pill">
                    <FontAwesomeIcon icon={faHourglassHalf} /> No upcoming class
                  </span>
                </div>
                <h2 id="rmsd-card-title" className="rmsd-card-title">
                  Your next class isn’t scheduled yet
                </h2>
                <p className="rmsd-card-text">Check your schedule, or message us to book the next one.</p>
                <div className="rmsd-cta-row">
                  <Link to="/student/schedule" className="rmsd-btn">
                    <FontAwesomeIcon icon={faCalendarAlt} /> My schedule
                  </Link>
                  <a
                    className="rmsd-btn rmsd-btn--wa"
                    href={waUrl(`Hi! I'm ${userData?.fullName || 'a student'} and I'd like to schedule my next class.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} /> Message us
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="rmsd-card-top">
                  {status === 'live' ? (
                    <span className="rmsd-pill is-live">
                      <span className="rmsd-live-dot" aria-hidden="true" /> Live now
                    </span>
                  ) : (
                    <span className="rmsd-pill">
                      <FontAwesomeIcon icon={faClock} /> Starts in {formatCountdown(start - now)}
                    </span>
                  )}
                </div>

                <div className="rmsd-next">
                  <div className="rmsd-next-text">
                    <h2 id="rmsd-card-title" className="rmsd-card-title">
                      {classTitle}
                    </h2>
                    <p className="rmsd-card-text">
                      Live on Zoom with <strong>{teacher}</strong>
                    </p>
                  </div>
                  {data.imagecourse && <img className="rmsd-next-img" src={data.imagecourse} alt="" />}
                </div>

                <ul className="rmsd-facts">
                  <li>
                    <FontAwesomeIcon icon={faCalendarAlt} /> {dateLabel}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faClock} /> {timeLabel} <small>your time</small>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faVideo} /> 50–60 min
                  </li>
                </ul>

                <button
                  type="button"
                  className="rmsd-btn rmsd-btn--join"
                  onClick={() => handleJoinClass(data.scheduleid, status === 'live')}
                  disabled={joining}
                >
                  {joining ? (
                    <span className="rmsd-spinner" aria-label="Opening class" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faVideo} />
                      Join class
                      <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </button>

                {status === 'upcoming' && (
                  <div className="rmsd-cal">
                    <span>Add to calendar:</span>
                    <a href={googleCalUrl} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faCalendarPlus} /> Google
                    </a>
                    <button type="button" onClick={downloadIcs}>
                      <FontAwesomeIcon icon={faCalendarPlus} /> Apple / Outlook
                    </button>
                  </div>
                )}

                <p className="rmsd-note">
                  <FontAwesomeIcon icon={faGlobe} /> Times in your time zone ({timeZone}). You can join {JOIN_EARLY_MIN}{' '}
                  minutes before the start.
                </p>
              </>
            )}
          </section>

          {/* ---------- Side ---------- */}
          <div className="rmsd-sidecol">
          <aside className="rmsd-side" aria-labelledby="rmsd-side-title">
            <h2 id="rmsd-side-title" className="rmsd-side-title">
              Before every class
            </h2>
            <ul className="rmsd-setup">
              <li>
                <span className="rmsd-setup-icon">
                  <FontAwesomeIcon icon={faLaptop} />
                </span>
                <div>
                  <strong>Computer ready</strong>
                  <p>
                    Charged, with Zoom installed.{' '}
                    <a href="https://zoom.us/download" target="_blank" rel="noopener noreferrer">
                      Get Zoom
                    </a>
                  </p>
                </div>
              </li>
              <li>
                <span className="rmsd-setup-icon">
                  <FontAwesomeIcon icon={faVolumeHigh} />
                </span>
                <div>
                  <strong>Sound and camera on</strong>
                  <p>In a quiet place.</p>
                </div>
              </li>
              <li>
                <span className="rmsd-setup-icon">
                  <FontAwesomeIcon icon={faDoorOpen} />
                </span>
                <div>
                  <strong>Join 5 minutes early</strong>
                  <p>So you don’t miss the start.</p>
                </div>
              </li>
            </ul>

            {data.Teacher && (
              <div className="rmsd-teacher">
                <span className="rmsd-teacher-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </span>
                <div>
                  <strong>{data.Teacher}</strong>
                  <span>Your teacher</span>
                </div>
              </div>
            )}
          </aside>

          {/* ---------- Wall of fame: who earned a badge or a medal lately ---------- */}
          <WallOfFame path="/api/student/awards/feed" />
          </div>
        </div>

        {/* ---------- Invite a friend (referral) ---------- */}
        <InviteFriends />

        {/* ---------- Recent classes ---------- */}
        <section className="rmsd-section" aria-labelledby="rmsd-recent-title">
          <div className="rmsd-section-head">
            <h2 id="rmsd-recent-title" className="rmsd-section-title">
              Recent classes
            </h2>
            <Link to="/student/myclasses" className="rmsd-more">
              All my classes <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>

          {recent.length === 0 ? (
            <p className="rmsd-empty">Your finished classes, notes and projects will show up here.</p>
          ) : (
            <ul className="rmsd-recent">
              {recent.map((c, i) => (
                <li key={`${c.CourseName}-${c.ClassDateTime}-${i}`} className="rmsd-recent-item">
                  <span className="rmsd-recent-check" aria-hidden="true">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </span>
                  <div className="rmsd-recent-body">
                    <div className="rmsd-recent-top">
                      <h3>{c.CourseName}</h3>
                      <span>{fmtDate(c.ClassDateTime, { month: 'short', day: 'numeric' })}</span>
                    </div>
                    {c.ModuleName && <p className="rmsd-recent-module">{c.ModuleName}</p>}
                    <p className="rmsd-recent-teacher">
                      with {[c.TeacherFirstName, c.TeacherLastName].filter(Boolean).join(' ')}
                    </p>
                    <Stars rating={c.Rating} />
                    {(c.CourseNote || c.CourseLink) && (
                      <div className="rmsd-recent-links">
                        {c.CourseNote && (
                          <a href={c.CourseNote} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon icon={faBook} /> Notes
                          </a>
                        )}
                        {c.CourseLink && (
                          <a href={c.CourseLink} target="_blank" rel="noopener noreferrer">
                            <FontAwesomeIcon icon={faExternalLinkAlt} /> My project
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ---------- Showcase (other students, privacy-safe) ---------- */}
        {showcase.length > 0 && (
          <section className="rmsd-section" aria-labelledby="rmsd-show-title">
            <h2 id="rmsd-show-title" className="rmsd-section-title">
              What other students are building
            </h2>
            <div className="rmsd-show-grid">
              {showcase.map((p, i) => (
                <article className="rmsd-show-card" key={`${p.CourseName}-${i}`}>
                  {p.CourseFilesLink && <img className="rmsd-show-img" src={p.CourseFilesLink} alt="" loading="lazy" />}
                  <div className="rmsd-show-body">
                    <h3>{p.CourseName}</h3>
                    {p.ModuleName && <p>{p.ModuleName}</p>}
                    <span className="rmsd-show-by">by {shortName(p.StudentFirstName, p.StudentLastName)}</span>
                    <Stars rating={p.Rating} />
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ---------- Join dialog ---------- */}
      {dialog && (
        <div className="rmsd-overlay" onClick={() => setDialog(null)}>
          <div
            className="rmsd-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rmsd-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="rmsd-dialog-close" onClick={() => setDialog(null)} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <span className={`rmsd-dialog-icon is-${dialog.type}`}>
              <FontAwesomeIcon
                icon={dialog.type === 'early' ? faHourglassHalf : dialog.type === 'expired' ? faCircleCheck : faTriangleExclamation}
              />
            </span>
            <h3 id="rmsd-dialog-title">
              {dialog.type === 'early' && 'Not time yet'}
              {dialog.type === 'expired' && 'This class has ended'}
              {dialog.type === 'error' && 'Couldn’t open the class'}
            </h3>
            {dialog.message && <p>{dialog.message}</p>}
            {dialog.type === 'early' && <p>You can join {JOIN_EARLY_MIN} minutes before the start time.</p>}
            <div className="rmsd-dialog-actions">
              {dialog.type === 'expired' && (
                <Link to="/student/schedule" className="rmsd-btn" onClick={() => setDialog(null)}>
                  <FontAwesomeIcon icon={faCalendarAlt} /> My schedule
                </Link>
              )}
              {dialog.type === 'error' && (
                <a className="rmsd-btn rmsd-btn--wa" href={waUrl('Hi! I can’t join my class.')} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faWhatsapp} /> Message us
                </a>
              )}
              <button type="button" className="rmsd-btn rmsd-btn--ghost" onClick={() => setDialog(null)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;